// Dev-only admin page for triggering email plumbing manually.
// Hard-disabled in production builds via NODE_ENV check on the server functions.
import { useState } from "react";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { sendTrialReminderManual } from "@/lib/trial-reminders.functions";
import { sendPaymentFailedManual } from "@/lib/payment-failed-email.functions";
import { sendCancellationManual } from "@/lib/cancellation-email.functions";

export const Route = createFileRoute("/admin/test")({
  beforeLoad: () => {
    if (process.env.NODE_ENV === "production") {
      throw redirect({ to: "/" });
    }
  },
  component: AdminTestPage,
});

type Mode = "trial" | "payment_failed" | "cancellation";

function AdminTestPage() {
  const sendTrial = useServerFn(sendTrialReminderManual);
  const sendPayFailed = useServerFn(sendPaymentFailedManual);
  const sendCancellation = useServerFn(sendCancellationManual);
  const [mode, setMode] = useState<Mode>("trial");
  const [identifier, setIdentifier] = useState("");
  const [reminderType, setReminderType] =
    useState<"day5" | "day7" | "day8">("day5");
  const defaultRetry = new Date(Date.now() + 3 * 86400000)
    .toISOString()
    .slice(0, 10);
  const defaultAccessEnd = new Date(Date.now() + 30 * 86400000)
    .toISOString()
    .slice(0, 10);
  const [retryDate, setRetryDate] = useState(defaultRetry);
  const [accessEndsAt, setAccessEndsAt] = useState(defaultAccessEnd);
  const [response, setResponse] = useState<unknown>(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setResponse(null);
    try {
      const value = identifier.trim();
      const isEmail = value.includes("@");
      const idPayload = isEmail ? { email: value } : { userId: value };
      let res: unknown;
      if (mode === "trial") {
        res = await sendTrial({ data: { ...idPayload, reminderType } });
      } else if (mode === "payment_failed") {
        res = await sendPayFailed({
          data: { ...idPayload, retryDate: retryDate || undefined },
        });
      } else {
        res = await sendCancellation({
          data: { ...idPayload, accessEndsAt: accessEndsAt || undefined },
        });
      }
      setResponse(res);
    } catch (err: any) {
      setResponse({ error: err?.message || String(err) });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ maxWidth: 640, margin: "40px auto", padding: 24, fontFamily: "system-ui" }}>
      <h1>Admin: Email test</h1>
      <p style={{ color: "#666" }}>
        Dev-only. Manually fire transactional emails for a specific user, bypassing
        eligibility checks and idempotency. Disabled in production.
      </p>
      <form onSubmit={handleSubmit} style={{ display: "grid", gap: 12, marginTop: 16 }}>
        <label>
          Email type
          <select
            value={mode}
            onChange={(e) => setMode(e.target.value as Mode)}
            style={{ display: "block", width: "100%", padding: 8, marginTop: 4 }}
          >
            <option value="trial">Trial reminder</option>
            <option value="payment_failed">Payment failed</option>
            <option value="cancellation">Cancellation confirmation</option>
          </select>
        </label>
        <label>
          Email or User ID (uuid)
          <input
            type="text"
            value={identifier}
            onChange={(e) => setIdentifier(e.target.value)}
            placeholder="user@example.com or uuid"
            required
            style={{ display: "block", width: "100%", padding: 8, marginTop: 4 }}
          />
        </label>
        {mode === "trial" && (
          <label>
            Reminder type
            <select
              value={reminderType}
              onChange={(e) => setReminderType(e.target.value as any)}
              style={{ display: "block", width: "100%", padding: 8, marginTop: 4 }}
            >
              <option value="day5">day5 (2 days left)</option>
              <option value="day7">day7 (ends tomorrow)</option>
              <option value="day8">day8 (trial ended)</option>
            </select>
          </label>
        )}
        {mode === "payment_failed" && (
          <label>
            Retry date (optional — leave blank for "no further retries")
            <input
              type="date"
              value={retryDate}
              onChange={(e) => setRetryDate(e.target.value)}
              style={{ display: "block", width: "100%", padding: 8, marginTop: 4 }}
            />
          </label>
        )}
        {mode === "cancellation" && (
          <label>
            Access ends at (default: 30 days from now)
            <input
              type="date"
              value={accessEndsAt}
              onChange={(e) => setAccessEndsAt(e.target.value)}
              style={{ display: "block", width: "100%", padding: 8, marginTop: 4 }}
            />
          </label>
        )}
        <button type="submit" disabled={loading} style={{ padding: "10px 16px" }}>
          {loading ? "Sending…" : "Send email"}
        </button>
      </form>
      {response !== null && (
        <pre
          style={{
            marginTop: 24,
            padding: 16,
            background: "#f4f4f4",
            border: "1px solid #ddd",
            borderRadius: 4,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            fontSize: 12,
          }}
        >
          {JSON.stringify(response, null, 2)}
        </pre>
      )}
    </div>
  );
}
